<script setup lang="ts">
    import {
        AlertDialog,
        AlertDialogAction,
        AlertDialogCancel,
        AlertDialogContent,
        AlertDialogDescription,
        AlertDialogFooter,
        AlertDialogHeader,
        AlertDialogTitle,
    } from '@/components/ui/alert-dialog';
    import { computed } from 'vue';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import { useMutation } from '@tanstack/vue-query';
    import { useQueryClient } from '@tanstack/vue-query';
    import axios from 'axios';
    import QueryKey from '@/enums/QueryKey';
    import type { Payslip, AuthUser } from '@/types';

    const { t } = useI18n();

    const queryClient = useQueryClient();

    const props = defineProps<{
        payslip: Payslip;
        modelValue?: boolean;
        user: AuthUser;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: boolean];
    }>();

    const open = computed({
        get: () => props.modelValue ?? false,
        set: (value) => emit('update:modelValue', value),
    });

    const { mutate, isPending } = useMutation({
        mutationFn: (formData: { id: number; approverUserName: string }) =>
            axios.post(route('v2.salary.payslip.approve'), formData),
        onSuccess: (data) => {
            toast.success(data.data.success);
            queryClient.invalidateQueries({
                queryKey: [QueryKey.PAYROLLS, props.user.selected_company_id],
            });
        },
        onError: (error: any) => {
            toast.error(error.response.data.error || t('forms.system_error'));
        },
    });

    function approvePayslip() {
        mutate({
            id: props.payslip.id,
            approverUserName: props.user.name,
        });
    }
</script>

<template>
    <AlertDialog
        :open="open"
        @update:open="(value) => (open = value)"
    >
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>{{ t('salary.pay_slips.js.approve.title') }}</AlertDialogTitle>
                <AlertDialogDescription>
                    {{ t('salary.pay_slips.js.approve.content') }}
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel>{{ t('forms.no') }}</AlertDialogCancel>
                <AlertDialogAction
                    @click="approvePayslip"
                    variant="default"
                >
                    {{ t('pay-slips.approve') }}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
</template>
